import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import ApplicantShell from "../components/ApplicantShell";
import RecruitmentShell from "../components/RecruitmentShell";
import ApplicantRejectionLetter from "../components/ApplicantRejectionLetter";
import {
  dismissUserNotification,
  getUserNotifications,
  isApiConfigured,
} from "../apiClient";
import NotificationListRowVisual from "../components/NotificationListRowVisual";
import {
  applicationRejectedJobPath,
  isApplicationRejectedNotification,
  notificationHasRejectionLetter,
} from "../lib/rejectionNotification";
import { isNotificationDismissible } from "../lib/applicantProfileNotification";
import { dismissNotificationIfViewed } from "../lib/notificationDismiss";
import { getNotificationVisualKind } from "../lib/notificationListDisplay";
import { notificationPageOpenPath } from "../lib/notificationNavigation";
import { parseSafeInternalNextPath } from "../navigateAfterAuth";

const PAGE_SIZE = 30;

function workspaceHome(role) {
  if (role === "hiring-manager" || role === "recruiter") return "/hiring-dashboard";
  return "/applicant-home";
}

function findNotificationForDeepLink(notifications, { jobPostId, notificationId }) {
  const nid = String(notificationId || "").trim();
  if (nid) {
    const byId = notifications.find((n) => n.id === nid);
    if (byId) return byId;
  }
  const id = String(jobPostId || "").trim();
  if (!id) return null;
  return (
    notifications.find(
      (n) =>
        n.type === "application.rejected" && String(n.payload?.jobPostId || "").trim() === id
    ) || null
  );
}

function NotificationsPageContent({ currentUser, authToken, accent, role }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightJobPostId = String(searchParams.get("jobPostId") || "").trim();
  const highlightNotificationId = String(searchParams.get("notificationId") || "").trim();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dismissingId, setDismissingId] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const loadLists = useCallback(async () => {
    const t = String(authToken || "").trim();
    if (!t || !isApiConfigured()) {
      setError("Not signed in.");
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await getUserNotifications(t, role, { status: "all", limit: PAGE_SIZE, offset: 0 });
      setItems(Array.isArray(res.notifications) ? res.notifications : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load notifications.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authToken, role]);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  useEffect(() => {
    if (loading) return;
    if (!highlightJobPostId && !highlightNotificationId) return;
    const match = findNotificationForDeepLink(items, {
      jobPostId: highlightJobPostId,
      notificationId: highlightNotificationId,
    });
    if (match) setSelectedId(match.id);
  }, [highlightJobPostId, highlightNotificationId, loading, items]);

  useEffect(() => {
    if (!selectedId || loading) return;
    const safeId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(selectedId)
        : String(selectedId).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    const el = document.querySelector(`[data-notification-id="${safeId}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedId, loading]);

  const handleOpen = async (notification) => {
    const path = parseSafeInternalNextPath(notificationPageOpenPath(notification, role)) || "";
    if (!path || !path.startsWith("/")) return;
    const dismissed = await dismissNotificationIfViewed(authToken, role, notification);
    if (dismissed) {
      await loadLists();
    }
    navigate(path);
  };

  const handleDismiss = async (notificationId) => {
    const t = String(authToken || "").trim();
    if (!t || dismissingId) return;
    setDismissingId(notificationId);
    try {
      await dismissUserNotification(t, role, notificationId);
      if (selectedId === notificationId) {
        setSelectedId("");
        setSearchParams({}, { replace: true });
      }
      await loadLists();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not dismiss notification.");
    } finally {
      setDismissingId("");
    }
  };

  const renderRow = (n) => {
    const isActive = n.isActive !== false && !n.dismissedAt && !n.actionedAt;
    const isSelected = selectedId === n.id;
    const isApplicationUpdate = isApplicationRejectedNotification(n);
    const hasLetter = notificationHasRejectionLetter(n);
    const jobPath = applicationRejectedJobPath(n);
    const kind = getNotificationVisualKind(n);
    const itemClass = [
      "jw-notification-item",
      "jw-notification-item--page",
      isActive ? `jw-notification-item--active-${accent}` : "jw-notification-item--history",
      `jw-notification-item--kind-${kind}`,
      isSelected ? "jw-notification-page__row--selected" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const createdAt = n.createdAt ? new Date(n.createdAt) : null;
    const dateLabel =
      createdAt && !Number.isNaN(createdAt.getTime())
        ? createdAt.toLocaleString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : null;

    const dismissControl =
      isActive && isNotificationDismissible(n) ? (
        <button
          type="button"
          className="jw-notification-page__dismiss small-outline-button"
          disabled={dismissingId === n.id}
          onClick={() => void handleDismiss(n.id)}
        >
          {dismissingId === n.id ? "Dismissing…" : "Dismiss"}
        </button>
      ) : null;

    return (
      <li key={n.id} className="jw-notification-page__row-wrap" data-notification-id={n.id}>
        {dateLabel ? (
          <time className="jw-notification-page__created" dateTime={n.createdAt}>
            {dateLabel}
          </time>
        ) : null}
        {isApplicationUpdate && hasLetter ? (
          <div
            className={[
              itemClass,
              "jw-notification-page__row",
              "jw-notification-page__row--with-letter",
              isApplicationUpdate && jobPath ? "jw-notification-page__row--navigable" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <button
              type="button"
              className="jw-notification-page__card jw-notification-page__open"
              onClick={() => void handleOpen(n)}
            >
              <div className="jw-notification-page__card-head">
                <NotificationListRowVisual notification={n} accent={accent} showChevron={false} />
              </div>
              <ApplicantRejectionLetter
                letterHtml={n.payload.fullMessageHtml}
                className="jw-notification-page__card-letter"
              />
            </button>
            {dismissControl}
          </div>
        ) : (
          <div className="jw-notification-page__row-actions">
            <button type="button" className={itemClass} onClick={() => void handleOpen(n)}>
              <NotificationListRowVisual notification={n} accent={accent} showChevron />
            </button>
            {dismissControl}
          </div>
        )}
      </li>
    );
  };

  return (
    <main
      className={
        accent === "p2"
          ? "jw-recruitment-main applicant-home jw-notifications-page"
          : "jw-applicant-main applicant-home jw-notifications-page"
      }
    >
      <section className="home-section">
        <Link className="back-link" to={workspaceHome(role)}>
          &larr; Back
        </Link>
        <h1>Notifications</h1>

        {error ? (
          <p className="subtitle" role="alert">
            {error}
          </p>
        ) : null}
        {loading ? <p className="subtitle">Loading…</p> : null}

        {!loading ? (
          <div className="jw-notification-page__section">
            {items.length === 0 ? (
              <p className="jw-notification-page__empty">No notifications yet.</p>
            ) : (
              <div className="jw-notification-page__panel">
                <ul className="jw-notification-page__list">{items.map((n) => renderRow(n))}</ul>
              </div>
            )}
          </div>
        ) : null}
      </section>
    </main>
  );
}

export default function NotificationsPage({ currentUser, setCurrentUser, authToken }) {
  const role = currentUser?.role;
  const isApplicant = role === "applicant";
  const isHiring = role === "hiring-manager" || role === "recruiter";
  const accent = isHiring ? "p2" : "p1";

  if (!isApplicant && !isHiring) {
    return null;
  }

  const content = (
    <NotificationsPageContent currentUser={currentUser} authToken={authToken} accent={accent} role={role} />
  );

  if (isHiring) {
    return (
      <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser}>
        {content}
      </RecruitmentShell>
    );
  }

  return (
    <ApplicantShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      {content}
    </ApplicantShell>
  );
}
